//libraries
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";

//Components
import Footer from "../components/Footer";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Navigation from "../components/navigation/Navigation";
import styles from "./NotFound.module.scss";

/**
 * Hand-written per locale, not machine-translated and not pulled from the blog or guide
 * dictionaries. Those dictionaries describe articles and destinations; this page describes a
 * mistake, and borrowing their keys is how `/discover` ended up with "The Stippl journal" over a
 * list of countries.
 */
interface NotFoundCopy {
	heading: string;
	body: string;
	home: string;
	blog: string;
	discover: string;
}

const COPY: Record<string, NotFoundCopy> = {
	en: {
		heading: "We couldn't find that page",
		body: "The link may be old, or the address may have a typo in it. Here is where most people go next.",
		home: "Go to the homepage",
		blog: "Read the travel guides",
		discover: "Browse every destination",
	},
	nl: {
		heading: "Deze pagina konden we niet vinden",
		body: "De link is misschien verouderd, of er zit een typefout in het adres. Dit is waar de meeste mensen daarna heen gaan.",
		home: "Naar de homepage",
		blog: "Lees de reisgidsen",
		discover: "Bekijk alle bestemmingen",
	},
	de: {
		heading: "Diese Seite konnten wir nicht finden",
		body: "Der Link ist vielleicht veraltet, oder in der Adresse steckt ein Tippfehler. Hier geht es für die meisten weiter.",
		home: "Zur Startseite",
		blog: "Zu den Reiseführern",
		discover: "Alle Reiseziele ansehen",
	},
	fr: {
		heading: "Nous n'avons pas trouvé cette page",
		body: "Le lien est peut-être ancien, ou l'adresse contient une faute de frappe. Voici où vont la plupart des visiteurs.",
		home: "Aller à l'accueil",
		blog: "Lire les guides de voyage",
		discover: "Parcourir toutes les destinations",
	},
	es: {
		heading: "No hemos encontrado esa página",
		body: "Puede que el enlace esté anticuado o que la dirección tenga una errata. Aquí es adonde va la mayoría.",
		home: "Ir a la página de inicio",
		blog: "Leer las guías de viaje",
		discover: "Explorar todos los destinos",
	},
	it: {
		heading: "Non abbiamo trovato questa pagina",
		body: "Il link potrebbe essere vecchio, oppure l'indirizzo contiene un errore di battitura. Ecco dove va la maggior parte delle persone.",
		home: "Vai alla home",
		blog: "Leggi le guide di viaggio",
		discover: "Sfoglia tutte le destinazioni",
	},
	pt: {
		heading: "Não encontrámos essa página",
		body: "O link pode estar desatualizado ou o endereço pode ter um erro. É aqui que a maioria das pessoas segue.",
		home: "Ir para a página inicial",
		blog: "Ler os guias de viagem",
		discover: "Ver todos os destinos",
	},
};

/** The copy for a locale, English when there is none. Exported so a caller can title the page to match its heading. */
export const getNotFoundCopy = (lang: string): NotFoundCopy => COPY[lang] || COPY.en;

interface NotFoundProps {
	/**
	 * The page's locale, passed by `MarketingShell.tsx` from the matched route's `langOverride`,
	 * NOT `i18n.language` — there is no i18next instance in the marketing bundle, so that resolves
	 * to `undefined` during SSR. `DiscoverHub.tsx` carries the same note; the short version is that
	 * reading i18next here would ship English copy and unprefixed links on every locale.
	 */
	lang?: string;
}

/**
 * The page an invalid marketing URL actually renders (123yb692x98).
 *
 * Until this existed, a 404 was correct in its status line and empty in its body: the response was
 * the bare 10,660-byte SPA shell, titled "Stippl: The All In One Adventure Planner", with no `<h1>`
 * and nothing but `<noscript>` and an empty root div. A crawler or a reader with JavaScript off saw
 * a blank page with the app's generic title. In a browser the SPA eventually painted an English
 * "Loading page.." spinner for several seconds and then the literal words "nothing found" — in
 * every locale — under a tab title auto-generated from the URL's own segments.
 *
 * It renders through the ordinary marketing route table rather than a special render mode, so it
 * gets the same chrome, the same locale handling and the same head builder as every other page.
 */
const NotFound = ({ lang }: NotFoundProps = {}) => {
	const currentLang = lang || "en";
	const copy = getNotFoundCopy(currentLang);
	// `localizeHref` prefixes only paths that have a real localised route — all three of these do,
	// as of the /discover hub landing (123yb6937h0). A locale with no route for one of them would
	// get the English page rather than a second 404, which is the right failure.
	const loc = (href: string) => localizeHref(href, currentLang as MarketingLangCode);

	return (
		<div id="stippl-not-found" className={styles.page}>
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			<div className={styles.container}>
				<span className={styles.code}>404</span>
				<h1 className={styles.heading}>{copy.heading}</h1>
				<p className={styles.body}>{copy.body}</p>

				{/* Real anchors, not buttons. This page is reached by crawlers following a stale link,
				    and a dead end is what turns one rotted URL into a crawl budget problem. */}
				<ul className={styles.links}>
					<li>
						<a href={loc("/")} className={styles.link}>
							{copy.home}
						</a>
					</li>
					<li>
						<a href={loc("/blog")} className={styles.link}>
							{copy.blog}
						</a>
					</li>
					<li>
						<a href={loc("/discover")} className={styles.link}>
							{copy.discover}
						</a>
					</li>
				</ul>
			</div>

			<Footer />
		</div>
	);
};

export default NotFound;
